import React, { useEffect, useState } from "react";
import { getUserDetails, getSubscriptionStatus } from "../../../../api/auth";
import { authenticateUser } from "../../../../redux/slices/authSlice";
import Sidebar from "../DashboardComponents/Sidebar";
import Box from "@mui/material/Box";
import { Outlet } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
    // NEW STATE: Tracks if the necessary async checks are complete
    const [isReady, setIsReady] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // --- CONSOLIDATED FUNCTION TO FETCH STATUS AND REDIRECT ---
    async function fetchAndApplyStatus() {
        let shouldRedirectToPayment = false;

        try {
            // 1. Fetch the full status object from the backend
            const result = await getSubscriptionStatus();

            // Log the result (for debugging)
            console.log("Full Subscription Status Result on Refresh:", result);

            // Extract the raw status string
            const rawStatus = result.rawStatus || "inactive";

            // 2. Decide redirection based on the boolean flag, which is more reliable than the raw string
            if (result.isActive) {
                console.log("Subscription Status: ACTIVE. Staying on dashboard.");
                shouldRedirectToPayment = false;
            } else {
                console.log(`Subscription Status: ${rawStatus}. Redirecting to payment.`);
                shouldRedirectToPayment = true;
            }

            // 3. Save the raw status string to localStorage for other components (like DashboardSettings)
            localStorage.setItem("subscription_status", rawStatus);

        } catch (error) {
            console.error("Failed to fetch subscription status:", error);

            // On API failure, assume inactive/error state and redirect to payment
            localStorage.setItem("subscription_status", "inactive_error");
            shouldRedirectToPayment = true;
        } finally {
            // 4. Perform Redirection
            if (shouldRedirectToPayment) {
                navigate("/dashboard/payment");
            }

            // 5. Mark checks as complete, regardless of the outcome
            // This is crucial for rendering the content (or the redirecting message)
            setIsReady(true);
        }
    }
    // ---------------------------------------------------------


    // useEffect 1: Handles status fetching and redirection
    useEffect(() => {
        fetchAndApplyStatus();
    }, []); // Runs only once on mount/refresh


    // useEffect 2: Handles user details and Redux authentication.
    useEffect(() => {
        async function fetchUserDetails() {
            try {
                const response = await getUserDetails();
                if (response.data.userId) {
                    dispatch(
                        authenticateUser({
                            userId: response.data.userId,
                            email: response.data.email,
                            role: response.data.role,
                            courseName: response.data.courseName,
                            universityName: response.data.universityName,
                            classYear: response.data.classYear,
                            customerID: response.data.customerID,
                            avatar: response.data.avatar,
                        })
                    );
                }
            } catch (error) {
                console.error("Error fetching details for authentication:", error);
            }
        }

        fetchUserDetails();
    }, [dispatch]);

    // --- RENDERING ---

    // Display a loading state or nothing until checks are complete
    if (!isReady) {
        // You can replace this with a better loading spinner if needed
        return <Box sx={{ p: 4 }}>Loading Dashboard...</Box>;
    }

    // Once isReady is true, either the user has been redirected (if inactive)
    // or the user is active and we render the full dashboard structure.
    return (
        <>
            <Box sx={{ display: "flex" }}>
                <Sidebar />
                <Box component="main" sx={{ flexGrow: 1, p: 0, marginTop: "60px" }}>
                    <Outlet />
                </Box>
            </Box>
        </>
    );
};

export default Dashboard;